// 나의 지원 현황 페이지
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Stack,
  Heading
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { getUserApplications, getJobPosting, updateApplicationStatus } from 'utils/firestoreUtils';
import { useAuth } from 'contexts/AuthContext';

function Applications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { currentUser } = useAuth();
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (currentUser) {
      loadApplications();
    }
  }, [currentUser]);

  async function loadApplications() {
    try {
      setLoading(true);
      const apps = await getUserApplications(currentUser.uid);
      
      // 각 신청에 대한 공고 정보 가져오기
      const appsWithJobs = await Promise.all(
        apps.map(async (app) => {
          const job = await getJobPosting(app.jobId);
          return { ...app, job };
        })
      );
      
      setApplications(appsWithJobs);
    } catch (error) {
      console.error('신청 내역 로딩 오류:', error);
      toast({
        title: '오류',
        description: '신청 내역을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleCancelApplication(applicationId) {
    try {
      await updateApplicationStatus(applicationId, 'canceled');
      toast({
        title: '성공',
        description: '지원을 취소했습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      loadApplications(); // 새로고침
    } catch (error) {
      console.error('취소 오류:', error);
      toast({
        title: '오류',
        description: '취소에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'applied': return 'blue';
      case 'selected': return 'green';
      case 'waitlist': return 'yellow';
      case 'rejected': return 'red';
      case 'canceled': return 'gray';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'applied': return '지원완료';
      case 'selected': return '선정';
      case 'waitlist': return '대기';
      case 'rejected': return '미선정';
      case 'canceled': return '취소됨';
      default: return status;
    }
  };

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 통계 카드 */}
      <Grid
        templateColumns={{ sm: '1fr', md: 'repeat(4, 1fr)' }}
        gap={6}
        mb={6}
      >
        <Card>
          <CardBody>
            <Stack spacing={2} align="center">
              <Text fontSize="sm" color="gray.500">전체 지원</Text>
              <Heading size="lg" color="blue.500">
                {applications.length}
              </Heading>
            </Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stack spacing={2} align="center">
              <Text fontSize="sm" color="gray.500">지원 대기중</Text>
              <Heading size="lg" color="blue.500">
                {applications.filter(a => a.status === 'applied').length}
              </Heading>
            </Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stack spacing={2} align="center">
              <Text fontSize="sm" color="gray.500">선정</Text>
              <Heading size="lg" color="green.500">
                {applications.filter(a => a.status === 'selected').length}
              </Heading>
            </Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stack spacing={2} align="center">
              <Text fontSize="sm" color="gray.500">미선정</Text>
              <Heading size="lg" color="red.500">
                {applications.filter(a => a.status === 'rejected').length}
              </Heading>
            </Stack>
          </CardBody>
        </Card>
      </Grid>

      {/* 신청 목록 */}
      <Card overflowX={{ sm: 'scroll', xl: 'hidden' }}>
        <CardHeader p="6px 0px 22px 0px">
          <Text fontSize="xl" color={textColor} fontWeight="bold">
            나의 지원 현황
          </Text>
        </CardHeader>

        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple" color={textColor}>
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}>공고명</Th>
                  <Th borderColor={borderColor}>위치</Th>
                  <Th borderColor={borderColor}>근무기간</Th>
                  <Th borderColor={borderColor}>지원일</Th>
                  <Th borderColor={borderColor}>상태</Th>
                  <Th borderColor={borderColor}>관리</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center">
                      로딩 중...
                    </Td>
                  </Tr>
                ) : applications.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center">
                      지원 내역이 없습니다.
                    </Td>
                  </Tr>
                ) : (
                  applications.map((app) => (
                    <Tr key={app.id}>
                      <Td borderColor={borderColor}>
                        {app.job?.title || '삭제된 공고'}
                      </Td>
                      <Td borderColor={borderColor}>
                        {app.job?.location || '-'}
                      </Td>
                      <Td borderColor={borderColor}>
                        {app.job ? `${app.job.periodStart} ~ ${app.job.periodEnd}` : '-'}
                      </Td>
                      <Td borderColor={borderColor}>
                        {app.appliedAt?.toDate?.()?.toLocaleDateString() || '-'}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Badge colorScheme={getStatusColor(app.status)}>
                          {getStatusText(app.status)}
                        </Badge>
                      </Td>
                      <Td borderColor={borderColor}>
                        {app.status === 'applied' && (
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleCancelApplication(app.id)}
                          >
                            취소
                          </Button>
                        )}
                        {app.status === 'selected' && (
                          <Button
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                          >
                            상세
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>
    </Flex>
  );
}

export default Applications;

